import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { User, TransportProvider, CarrierCoverage } from '../../types'
import { isSuperAdmin, hasPermission } from '../../lib/permissions'
import { usersService, AdminUser } from '../../services/usersService'
import { auditService, AuditEntry } from '../../services/auditService'
import { notificationsService, NotificationConfigRow } from '../../services/notificationsService'
import { sendSMS, formatPhoneNumber } from '../../services/smsService'
import UserEditPanel from './UserEditPanel'
import SmsActivityPanel from './SmsActivityPanel'
import ContactsTab from './ContactsTab'
import { toast } from '../Toast'
import { forgetPlatformDates } from '../../services/platformSettingsService'
import './AdminView.css'

type Tab = 'users' | 'notifications' | 'audit' | 'settings' | 'contacts' | 'transport' | 'messaging'

interface AdminViewProps {
  user: User
}

export default function AdminView({ user }: AdminViewProps) {
  const canUsers = isSuperAdmin(user) || hasPermission(user, 'perm-user-management')
  const canNotif = isSuperAdmin(user) || hasPermission(user, 'perm-config-notifications')
  const canAudit = isSuperAdmin(user) || hasPermission(user, 'perm-view-audit')
  const availableTabs: { id: Tab; label: string }[] = [
    ...(canUsers ? [{ id: 'users' as Tab, label: '👥 Users & Permissions' }] : []),
    ...(canNotif ? [{ id: 'notifications' as Tab, label: '📧 Notifications' }] : []),
    ...(canAudit ? [{ id: 'audit' as Tab, label: '📋 Audit Log' }] : []),
    ...(canUsers ? [{ id: 'settings' as Tab, label: '⚙️ Settings' }] : []),
    // Winnie, 15 Sep: "Add Contacts as a new Admin nav item, alongside Users & Permissions,
    // Notifications, Audit Log, Settings, Transport Panel, and SMS."
    ...(canUsers ? [{ id: 'contacts' as Tab, label: '📇 Contacts' }] : []),
    ...(canUsers ? [{ id: 'transport' as Tab, label: '🚚 Transport Panel' }] : []),
    ...(canUsers ? [{ id: 'messaging' as Tab, label: '💬 SMS' }] : []),
  ]

  const [tab, setTab] = useState<Tab>(availableTabs[0]?.id ?? 'users')

  const canAccess = availableTabs.length > 0

  /**
   * This view used to own the only toast in the platform — a local one, rendered inside the
   * Admin screen and invisible everywhere else. Winnie's 15 Sep note asks for one confirmation
   * pattern across the whole platform, so it now hands off to the shared host in
   * src/components/Toast.tsx and the local copy is gone.
   */
  const showToast = (msg: string, error?: boolean) => {
    if (error) toast.error(msg)
    else toast.show(msg)
  }

  if (!canAccess) {
    return (
      <div className="admin-view">
        <div className="admin-alert error">⛔ Admin access required.</div>
      </div>
    )
  }

  return (
    <div className="admin-view">
      <h2 className="page-title">Admin</h2>
      <p className="page-description">Manage system settings, users, and permissions</p>

      <div className="admin-tabs">
        {availableTabs.map((t) => (
          <button key={t.id} className={`admin-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'users' && <UsersTab user={user} showToast={showToast} />}
      {tab === 'notifications' && <NotificationsTab user={user} showToast={showToast} />}
      {tab === 'audit' && <AuditTab />}
      {tab === 'settings' && <SettingsTab user={user} showToast={showToast} />}
      {tab === 'contacts' && <ContactsTab />}
      {tab === 'transport' && <TransportPanelTab showToast={showToast} />}
      {tab === 'messaging' && <MessagingTab showToast={showToast} />}
    </div>
  )
}

/* ---------------- Users tab ---------------- */
function UsersTab({ user, showToast }: { user: User; showToast: (m: string, e?: boolean) => void }) {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [panelOpen, setPanelOpen] = useState(false)
  const [editing, setEditing] = useState<AdminUser | null>(null)
  const [invitingId, setInvitingId] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      setUsers(await usersService.listUsers())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    load()
  }, [])

  const filtered = useMemo(() => {
    const t = search.toLowerCase()
    return users.filter((u) => !t || u.full_name.toLowerCase().includes(t) || u.email.toLowerCase().includes(t))
  }, [users, search])

  const openAdd = () => {
    setEditing(null)
    setPanelOpen(true)
  }
  const openEdit = (u: AdminUser) => {
    setEditing(u)
    setPanelOpen(true)
  }
  const onSaved = (msg: string, isError?: boolean) => {
    showToast(msg, isError)
    if (!isError) {
      setPanelOpen(false)
      load()
    }
  }

  const roleLabel = (u: AdminUser) => (u.is_super_admin ? 'Super Admin' : u.role)

  return (
    <>
      <div className="admin-toolbar">
        <button className="abtn primary" onClick={openAdd}>
          + Add User
        </button>
        <input className="admin-search" placeholder="Search users…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {error && <div className="admin-alert error">{error}</div>}

      {loading ? (
        <div className="admin-alert">Loading users…</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: '24%' }}>Name</th>
                <th style={{ width: '26%' }}>Email</th>
                <th style={{ width: '14%' }}>Mobile</th>
                <th style={{ width: '13%' }}>Role</th>
                <th style={{ width: '10%' }}>Status</th>
                <th style={{ width: '20%' }}>Sign-in</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="clickable" onClick={() => openEdit(u)}>
                  <td>
                    <div className="admin-uname">{u.full_name}</div>
                  </td>
                  <td className="admin-uemail" style={{ marginTop: 0 }}>{u.email}</td>
                  {/* Winnie, 15 Sep: mobile is now required on a user, so it is worth seeing
                      which of the existing accounts predate that and still have none. */}
                  <td className="admin-mono">
                    {u.phone || <span style={{ color: 'var(--red)' }}>Missing</span>}
                  </td>
                  <td>
                    <span className={`admin-badge ${u.is_super_admin ? 'super' : ''}`}>{roleLabel(u)}</span>
                  </td>
                  <td>
                    <span className={u.is_active ? 'admin-status-active' : 'admin-status-inactive'}>{u.is_active ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      className="abtn small"
                      disabled={invitingId === u.id}
                      title="Emails this user a link to choose their own password"
                      onClick={async () => {
                        setInvitingId(u.id)
                        const res = await usersService.sendPasswordSetupEmail(u.email)
                        showToast(res.message, !res.success)
                        setInvitingId(null)
                      }}
                    >
                      {invitingId === u.id ? 'Sending…' : 'Send set-password email'}
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ color: 'var(--txt3)' }}>
                    No users match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {panelOpen && <UserEditPanel actor={user} editing={editing} onClose={() => setPanelOpen(false)} onSaved={onSaved} />}
    </>
  )
}

/* ---------------- Notifications tab ---------------- */
function NotificationsTab({ user, showToast }: { user: User; showToast: (m: string, e?: boolean) => void }) {
  const [rows, setRows] = useState<NotificationConfigRow[]>([])
  const [loading, setLoading] = useState(true)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      setRows(await notificationsService.list())
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to load config', true)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    load()
  }, [])

  const startEdit = (r: NotificationConfigRow) => {
    setEditingKey(r.key)
    setEditValue(r.value)
  }
  const saveEdit = async (key: string) => {
    try {
      await notificationsService.setValue(key, editValue.trim(), user)
      await auditService.log(user, 'notification_updated', { details: { key, value: editValue.trim() } })
      toast.saved()
      setEditingKey(null)
      load()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Update failed', true)
    }
  }

  return (
    <>
      <p className="page-description">Configure email recipients and contact details for notifications.</p>
      {loading ? (
        <div className="admin-alert">Loading…</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: '35%' }}>Notification</th>
                <th style={{ width: '45%' }}>Value</th>
                <th style={{ width: '20%' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td>
                    <div className="admin-uname">{r.label || r.key}</div>
                    <div className="admin-uemail" style={{ marginTop: 3 }}>{r.description}</div>
                  </td>
                  <td>
                    {editingKey === r.key ? (
                      <input className="admin-input" value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus />
                    ) : (
                      <span className="admin-mono">{r.value}</span>
                    )}
                  </td>
                  <td>
                    {editingKey === r.key ? (
                      <span style={{ display: 'flex', gap: 6 }}>
                        <button className="abtn primary" style={{ padding: '6px 12px' }} onClick={() => saveEdit(r.key)}>
                          Save
                        </button>
                        <button className="abtn" style={{ padding: '6px 12px' }} onClick={() => setEditingKey(null)}>
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button className="abtn" style={{ padding: '6px 12px' }} onClick={() => startEdit(r)}>
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

/* ---------------- Audit tab ---------------- */
function AuditTab() {
  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      setEntries(await auditService.list({ from: from || undefined, to: to || undefined }))
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to])

  const fmt = (iso: string) => new Date(iso).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })
  const detailText = (e: AuditEntry) => {
    const d = e.details || {}
    if (d.key) return `${d.key} → ${d.value}`
    if (typeof d.is_super_admin === 'boolean') return `super admin: ${d.is_super_admin}${d.is_active === false ? ', deactivated' : ''}`
    if (d.email) return d.email
    return ''
  }

  return (
    <>
      <div className="admin-toolbar">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--txt2)' }}>
          <span>Filter:</span>
          <input type="date" className="admin-search" style={{ minWidth: 0 }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <span>to</span>
          <input type="date" className="admin-search" style={{ minWidth: 0 }} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>
      {loading ? (
        <div className="admin-alert">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="admin-alert">No audit entries in this range.</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: '18%' }}>Date</th>
                <th style={{ width: '18%' }}>By</th>
                <th style={{ width: '28%' }}>Action</th>
                <th style={{ width: '36%' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="admin-mono">{fmt(e.created_at)}</td>
                  <td>{e.actor_name || '—'}</td>
                  <td>
                    <span className="admin-uname">{e.action.replace(/_/g, ' ')}</span>
                    {e.target_name ? <div className="admin-uemail" style={{ marginTop: 2 }}>{e.target_name}</div> : null}
                  </td>
                  <td style={{ color: 'var(--txt2)' }}>{detailText(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

/* ---------------- Settings tab (folds existing admin_settings) ---------------- */
interface SettingsRow {
  id: number
  margin_threshold_percent: number
  margin_threshold_dollars: number
  ppsr_check_enabled: boolean
  auto_ppsr_on_creation: boolean
  /* Winnie, 13 Sep: the lot load fee is a dollar amount set by hand here and applied to
     every vehicle automatically at Paid - Ready for Sale. */
  lot_load_fee: number
  /* Winnie, 18 Sep. Two dates that turn on two controls, in the screen that already holds
     the other settings rather than in two new ones. */
  accounting_lock_date: string | null
  ppsr_discharge_gate_go_live: string | null
}
function SettingsTab({ user, showToast }: { user: User; showToast: (m: string, e?: boolean) => void }) {
  const [s, setS] = useState<SettingsRow | null>(null)
  const [phone, setPhone] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        const { data } = await supabase.from('admin_settings').select('*').single()
        if (data) setS(data as SettingsRow)
        setPhone(await notificationsService.getValue('company_phone', ''))
      } catch (e) {
        showToast(e instanceof Error ? e.message : 'Failed to load settings', true)
      } finally {
        setLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const save = async () => {
    if (!s) return
    setSaving(true)
    try {
      const { error } = await supabase
        .from('admin_settings')
        .update({
          margin_threshold_percent: s.margin_threshold_percent,
          margin_threshold_dollars: s.margin_threshold_dollars,
          ppsr_check_enabled: s.ppsr_check_enabled,
          auto_ppsr_on_creation: s.auto_ppsr_on_creation,
          lot_load_fee: s.lot_load_fee,
          accounting_lock_date: s.accounting_lock_date || null,
          ppsr_discharge_gate_go_live: s.ppsr_discharge_gate_go_live || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', s.id)
      if (error) throw error
      await notificationsService.setValue('company_phone', phone.trim(), user)
      await auditService.log(user, 'settings_updated', { details: { margin_percent: s.margin_threshold_percent, company_phone: phone.trim() } })
      /* Both dates are cached for the life of the page - drop it, or the very next correction
         or settlement reads the figure this screen just replaced. */
      forgetPlatformDates()
      toast.saved()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Save failed', true)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="admin-alert">Loading…</div>
  if (!s) return <div className="admin-alert error">Settings unavailable.</div>

  return (
    <div className="admin-form">
      <div className="admin-field">
        <label>Company Phone (buyer-facing emails)</label>
        <input className="admin-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </div>
      <div className="admin-field">
        <label>Lot Load Fee ($ per vehicle)</label>
        <input
          className="admin-input"
          type="number"
          min={0}
          step="0.01"
          value={s.lot_load_fee ?? 0}
          onChange={(e) => setS({ ...s, lot_load_fee: parseFloat(e.target.value) || 0 })}
        />
        <p className="admin-hint">
          Charged to every vehicle automatically the moment it reaches Paid – Ready for Sale,
          so nobody has to add it per car. It appears on Cost of Sales as a lot load fee and
          counts toward that vehicle’s landed cost. Set it to 0 to stop charging it; changing
          it does not alter fees already applied.
        </p>
      </div>
      <div className="admin-field">
        <label>Accounting Lock Date</label>
        <input
          className="admin-input"
          type="date"
          value={(s.accounting_lock_date || '').slice(0, 10)}
          onChange={(e) => setS({ ...s, accounting_lock_date: e.target.value || null })}
        />
        <p className="admin-hint">
          Everything dated on or before this is in a closed accounting period. An Admin cannot correct a
          sale price, sold date, purchase price or purchase date that falls inside it — a Super Admin can,
          and the correction is recorded as having overridden the lock. Leave it empty while nothing is
          closed.
        </p>
      </div>
      <div className="admin-field">
        <label>PPSR Discharge Gate — go-live date</label>
        <input
          className="admin-input"
          type="date"
          value={(s.ppsr_discharge_gate_go_live || '').slice(0, 10)}
          onChange={(e) => setS({ ...s, ppsr_discharge_gate_go_live: e.target.value || null })}
        />
        <p className="admin-hint">
          From this date, a vehicle only closes out when it is paid in full <strong>and</strong> its PPSR
          registration has been discharged. Vehicles that reached Sold – Delivered before it settle on
          payment alone, so turning this on cannot re-open cars that are already finished. Leave it empty
          and the rule is off.
        </p>
      </div>
      <div className="admin-field">
        <label>Margin Warning — Percent</label>
        <input
          className="admin-input"
          type="number"
          value={s.margin_threshold_percent}
          onChange={(e) => setS({ ...s, margin_threshold_percent: parseFloat(e.target.value) || 0 })}
        />
      </div>
      <div className="admin-field">
        <label>Margin Warning — Dollars</label>
        <input
          className="admin-input"
          type="number"
          value={s.margin_threshold_dollars}
          onChange={(e) => setS({ ...s, margin_threshold_dollars: parseFloat(e.target.value) || 0 })}
        />
      </div>
      <label className="admin-check-row">
        <input type="checkbox" checked={s.ppsr_check_enabled} onChange={(e) => setS({ ...s, ppsr_check_enabled: e.target.checked })} />
        <span>Compulsory PPSR check enabled</span>
      </label>
      <label className="admin-check-row">
        <input type="checkbox" checked={s.auto_ppsr_on_creation} onChange={(e) => setS({ ...s, auto_ppsr_on_creation: e.target.checked })} />
        <span>Auto-run PPSR on deal creation</span>
      </label>
      <button className="abtn primary" style={{ marginTop: 14 }} onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save Changes'}
      </button>
    </div>
  )
}


/* ==========================================================================
 * §5.1 — the pre-approved transport panel
 *
 * The carriers and the states they cover live in the database rather than in
 * code, because which carrier covers which state changes and a deploy is a
 * silly way to record that. The five in the spec are seeded by migration 023.
 * ========================================================================== */

const COVERAGE_LABEL: Record<CarrierCoverage, string> = {
  FreightIn: 'Freight in',
  FreightOut: 'Freight out',
  Both: 'Both',
}
const COVERAGE_OPTIONS: CarrierCoverage[] = ['Both', 'FreightIn', 'FreightOut']

const statesFromText = (text: string): string[] =>
  text
    .split(/[,\s]+/)
    .map((x) => x.trim().toUpperCase())
    .filter(Boolean)

const EMPTY_CARRIER = {
  name: '',
  scope: 'local',
  coverage: 'Both' as CarrierCoverage,
  states: '',
  contact_email: '',
  contact_phone: '',
}

function TransportPanelTab({ showToast }: { showToast: (m: string, e?: boolean) => void }) {
  const [rows, setRows] = useState<TransportProvider[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [draft, setDraft] = useState({ ...EMPTY_CARRIER })
  /* Winnie, 15 Sep: "Add an Edit action per carrier row — currently only an Active/Inactive
     toggle exists; Scope, States, Coverage, and Contact should all be editable on an existing
     carrier, not just settable at creation." Edited in place on the row, because the panel is
     five rows long and a slide-over panel for five fields is ceremony. */
  const [editId, setEditId] = useState<string | null>(null)
  const [edit, setEdit] = useState({ ...EMPTY_CARRIER })

  const load = () => {
    setLoading(true)
    supabase
      .from('transport_providers')
      .select('*')
      .order('scope')
      .order('name')
      .then(({ data }) => {
        setRows((data || []) as TransportProvider[])
        setLoading(false)
      })
  }

  useEffect(load, [])

  const add = async () => {
    if (!draft.name.trim()) return showToast('The carrier needs a name.', true)
    setBusy(true)
    const { error } = await supabase.from('transport_providers').insert([
      {
        name: draft.name.trim(),
        scope: draft.scope,
        coverage: draft.coverage,
        states: statesFromText(draft.states),
        contact_email: draft.contact_email.trim() || null,
        contact_phone: draft.contact_phone.trim() || null,
      },
    ])
    setBusy(false)
    if (error) return showToast(error.message, true)
    setDraft({ ...EMPTY_CARRIER })
    toast.saved()
    load()
  }

  const startEdit = (p: TransportProvider) => {
    setEditId(p.id)
    setEdit({
      name: p.name,
      scope: p.scope,
      coverage: p.coverage || 'Both',
      states: (p.states || []).join(' '),
      contact_email: p.contact_email || '',
      contact_phone: p.contact_phone || '',
    })
  }

  const saveEdit = async () => {
    if (!editId) return
    if (!edit.name.trim()) return showToast('The carrier needs a name.', true)
    setBusy(true)
    const { error } = await supabase
      .from('transport_providers')
      .update({
        name: edit.name.trim(),
        scope: edit.scope,
        coverage: edit.coverage,
        states: statesFromText(edit.states),
        contact_email: edit.contact_email.trim() || null,
        contact_phone: edit.contact_phone.trim() || null,
      })
      .eq('id', editId)
    setBusy(false)
    if (error) return showToast(error.message, true)
    setEditId(null)
    toast.saved()
    load()
  }

  const toggle = async (p: TransportProvider) => {
    const { error } = await supabase.from('transport_providers').update({ active: !p.active }).eq('id', p.id)
    if (error) return showToast(error.message, true)
    toast.saved()
    load()
  }

  if (loading) return <div className="admin-loading">Loading the transport panel…</div>

  return (
    <div className="admin-section">
      <h3>Transport panel</h3>
      <p className="admin-hint">
        The carriers offered when a sale is priced. A national carrier is offered first, then
        whoever is local to the delivery state. Deactivating one keeps its history on past sales.
        This panel is the only carrier list in the platform — the freight-out card on Sale Details
        reads the carriers below whose coverage is Freight out or Both, rather than keeping a list
        of its own.
      </p>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Carrier</th>
            <th>Scope</th>
            <th>Coverage</th>
            <th>States</th>
            <th>Contact</th>
            <th>Active</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) =>
            editId === p.id ? (
              <tr key={p.id}>
                <td>
                  <input className="admin-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                </td>
                <td>
                  <select className="admin-input" value={edit.scope} onChange={(e) => setEdit({ ...edit, scope: e.target.value })}>
                    <option value="local">Local</option>
                    <option value="national">National</option>
                  </select>
                </td>
                <td>
                  <select
                    className="admin-input"
                    value={edit.coverage}
                    onChange={(e) => setEdit({ ...edit, coverage: e.target.value as CarrierCoverage })}
                  >
                    {COVERAGE_OPTIONS.map((c) => (
                      <option key={c} value={c}>
                        {COVERAGE_LABEL[c]}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input className="admin-input" value={edit.states} onChange={(e) => setEdit({ ...edit, states: e.target.value })} />
                </td>
                <td>
                  <input
                    className="admin-input"
                    placeholder="Email"
                    value={edit.contact_email}
                    onChange={(e) => setEdit({ ...edit, contact_email: e.target.value })}
                  />
                  <input
                    className="admin-input"
                    style={{ marginTop: 6 }}
                    placeholder="Phone"
                    value={edit.contact_phone}
                    onChange={(e) => setEdit({ ...edit, contact_phone: e.target.value })}
                  />
                </td>
                <td>
                  <button className="admin-btn small" onClick={() => toggle(p)} disabled={busy}>
                    {p.active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="abtn primary small" onClick={saveEdit} disabled={busy}>
                    Save
                  </button>{' '}
                  <button className="abtn small" onClick={() => setEditId(null)} disabled={busy}>
                    Cancel
                  </button>
                </td>
              </tr>
            ) : (
              <tr key={p.id}>
                <td><b>{p.name}</b></td>
                <td>{p.scope === 'national' ? 'National' : 'Local'}</td>
                <td>{COVERAGE_LABEL[p.coverage || 'Both']}</td>
                <td>{(p.states || []).join(', ') || '—'}</td>
                <td>{p.contact_email || p.contact_phone || '—'}</td>
                <td>
                  <button className="admin-btn small" onClick={() => toggle(p)}>
                    {p.active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td>
                  <button className="abtn small" onClick={() => startEdit(p)}>
                    Edit
                  </button>
                </td>
              </tr>
            )
          )}
          {rows.length === 0 && (
            <tr><td colSpan={7}>No carriers yet — migration 023 seeds the five from the spec.</td></tr>
          )}
        </tbody>
      </table>

      <h4 style={{ marginTop: 20 }}>Add a carrier</h4>
      <div className="admin-form-row">
        <input placeholder="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        <select value={draft.scope} onChange={(e) => setDraft({ ...draft, scope: e.target.value })}>
          <option value="local">Local</option>
          <option value="national">National</option>
        </select>
        <select
          value={draft.coverage}
          onChange={(e) => setDraft({ ...draft, coverage: e.target.value as CarrierCoverage })}
        >
          {COVERAGE_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {COVERAGE_LABEL[c]}
            </option>
          ))}
        </select>
        <input placeholder="States, e.g. VIC NSW" value={draft.states} onChange={(e) => setDraft({ ...draft, states: e.target.value })} />
        <input placeholder="Email" value={draft.contact_email} onChange={(e) => setDraft({ ...draft, contact_email: e.target.value })} />
        <input placeholder="Phone" value={draft.contact_phone} onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })} />
        <button className="admin-btn" onClick={add} disabled={busy}>Add</button>
      </div>
    </div>
  )
}


/* ==========================================================================
 * SMS tab
 *
 * A test send, and the provider's answer printed verbatim.
 *
 * The reason this exists rather than someone poking the endpoint from a
 * console: when an SMS does not arrive there are three quite different causes
 * — the provider is still the mock, the credentials are wrong, or the message
 * was rejected — and they are indistinguishable unless you can see what came
 * back. The raw response is also the only way to learn Tall Bob's success
 * shape, which they do not document.
 * ========================================================================== */

const DEFAULT_TEST_MESSAGE =
  'Direct Auto DMS test message. If you received this, SMS is working.'

function MessagingTab({ showToast }: { showToast: (m: string, e?: boolean) => void }) {
  const [to, setTo] = useState('')
  const [message, setMessage] = useState(DEFAULT_TEST_MESSAGE)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{
    ok: boolean
    text: string
    provider?: string | null
    raw?: unknown
  } | null>(null)

  const normalised = formatPhoneNumber(to)

  const send = async () => {
    if (!normalised) {
      showToast('That is not a mobile number this system can use.', true)
      return
    }
    if (!message.trim()) {
      showToast('Write something to send.', true)
      return
    }
    setBusy(true)
    setResult(null)
    const r = await sendSMS({ to: normalised, message: message.trim(), trigger: 'admin_test' })
    setBusy(false)
    setResult({ ok: r.success, text: r.message, provider: r.provider, raw: r.raw })
    showToast(r.success ? 'Sent — see the response below.' : r.message, !r.success)
  }

  return (
    <div className="admin-section">
      <h3>SMS</h3>
      <p className="admin-hint">
        Sends one real message through whichever provider this environment is configured for,
        and writes it to the SMS log like any other send. If the provider comes back as
        <b> mock</b>, nothing left the building — set <code>SMS_PROVIDER</code> and the
        provider's credentials on the Cloudflare Pages project first.
      </p>

      <div className="admin-form-row">
        <input
          placeholder="Mobile, e.g. 0412 345 678"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          style={{ minWidth: 200 }}
        />
        <button className="admin-btn" onClick={send} disabled={busy}>
          {busy ? 'Sending…' : 'Send test'}
        </button>
      </div>
      <p className="admin-hint" style={{ marginTop: 4 }}>
        {to.trim()
          ? normalised
            ? `Will send to ${normalised}`
            : 'Not a usable Australian number.'
          : 'Australian mobiles only.'}
      </p>

      <textarea
        className="admin-textarea"
        rows={3}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />
      <p className="admin-hint">
        {message.length} characters
        {message.length > 160 ? ' — over 160, so it will be billed as more than one message.' : ''}
      </p>

      {result && (
        <div className={`sms-result ${result.ok ? 'ok' : 'bad'}`}>
          <div className="sms-result-head">
            <b>{result.ok ? 'Accepted by the provider' : 'Not sent'}</b>
            <span>{result.provider ? `via ${result.provider}` : 'no provider reported'}</span>
          </div>
          <p>{result.text}</p>
          {result.provider === 'mock' && (
            <p className="sms-result-warn">
              This environment is still on the mock provider — the message was logged, not sent.
            </p>
          )}
          {result.raw != null && (
            <>
              <div className="sms-result-label">What the provider returned</div>
              <pre>{JSON.stringify(result.raw, null, 2)}</pre>
            </>
          )}
        </div>
      )}

      <SmsActivityPanel showToast={showToast} />
    </div>
  )
}
